import { FormControlLabel, makeStyles, Switch } from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import TextField from "@material-ui/core/TextField";
import CardTitle from "@saleor/components/CardTitle";
import ControlledCheckbox from "@saleor/components/ControlledCheckbox";
import { FormSpacer } from "@saleor/components/FormSpacer";
import RadioGroupField from "@saleor/components/RadioGroupField";
import { RequirementsPicker } from "@saleor/discounts/types";
import { DiscountErrorFragment } from "@saleor/fragments/types/DiscountErrorFragment";
import { ChangeEvent } from "@saleor/hooks/useForm";
import {
  VoucherClassificationEnum,
  VoucherTypeEnum
} from "@saleor/types/globalTypes";
import { getFormErrors } from "@saleor/utils/errors";
import getDiscountErrorMessage from "@saleor/utils/errors/discounts";
import React, { useEffect } from "react";
import { useIntl } from "react-intl";

import { VoucherDetailsPageFormData } from "../VoucherDetailsPage";

interface VoucherRequirementsProps {
  data: VoucherDetailsPageFormData;
  defaultCurrency: string;
  disabled: boolean;
  errors: DiscountErrorFragment[];
  clientErrors?: {
    minSpent?: string;
    minTenants?: string;
    minCheckoutItemsQuantity?: string;
  };
  onChange: (event: ChangeEvent<any>) => void;
}

const useStyles = makeStyles(
  {
    minTenantsTextField: {
      display: "block",
      marginTop: "18px"
    },
    minTenantsWrapper: {
      borderTop: "1px solid rgb(234, 234, 234)"
    }
  },
  { name: "VoucherRequirements" }
);

const VoucherRequirements = ({
  data,
  disabled,
  errors,
  clientErrors,
  onChange
}: VoucherRequirementsProps) => {
  const intl = useIntl();
  const classes = useStyles("");

  useEffect(() => {
    if (data.discountType.toString() === "E_VOUCHER") {
      const newEvent = {
        target: { name: "requirementsPicker", value: RequirementsPicker.NONE }
      };
      onChange(newEvent);

      const newMinEvent = {
        target: { name: "hasMinTenants", value: false }
      };
      onChange(newMinEvent);
    }
  }, [data.discountType]);

  useEffect(() => {
    if (
      (data.type.toString() === "ENTIRE_ORDER" &&
        data.discountType.toString() !== "E_VOUCHER") ||
      data.discountType.toString() === "SHIPPING"
    ) {
      onChange({
        target: {
          name: "isAppliedOnEligibleItems",
          value: false
        }
      });
    } else if (
      data.type.toString() === "SPECIFIC_PRODUCT" ||
      data.discountType.toString() === "E_VOUCHER"
    ) {
      onChange({
        target: {
          name: "isAppliedOnEligibleItems",
          value: true
        }
      });
    }
  }, [data.requirementsPicker, data.type, data.discountType]);

  useEffect(() => {
    if (data.discountType.toString() === "SHIPPING") {
      onChange({
        target: {
          name: "type",
          value: VoucherTypeEnum.ENTIRE_ORDER
        }
      });
    }
  }, [data.discountType, data.type]);

  const formErrors = getFormErrors(
    ["minSpent", "minCheckoutItemsQuantity"],
    errors
  );

  const minimalOrderValueText = intl.formatMessage({
    defaultMessage: "Minimal order value",
    description: "voucher requirement"
  });
  const minimalQuantityText = intl.formatMessage({
    defaultMessage: "Minimum quantity of items",
    description: "voucher requirement"
  });

  const requirementsPickerChoices = [
    {
      label: intl.formatMessage({
        defaultMessage: "None",
        description: "voucher has no requirements"
      }),
      value: RequirementsPicker.NONE
    },
    {
      label: minimalOrderValueText,
      value: RequirementsPicker.ORDER
    },
    {
      label: minimalQuantityText,
      value: RequirementsPicker.ITEM
    }
  ];

  // On Click of SM Fulfilled, "Minumum Number of Tenants" is disabled and false
  React.useEffect(() => {
    if (data.verticalType === "SM_FULFILLED") {
      onChange({
        target: {
          name: "hasMinTenants",
          value: false
        }
      });
      onChange({
        target: {
          name: "minTenants",
          value: 0
        }
      });
    }
  }, [data.verticalType]);

  const showToggleSwitch =
    data.type === VoucherTypeEnum.SPECIFIC_PRODUCT ||
    data.discountType.toString() === "E_VOUCHER" ||
    data.type === VoucherTypeEnum.E_VOUCHER;
  return (
    <Card>
      <CardTitle
        title={intl.formatMessage({
          defaultMessage: "Minimum Requirements",
          description: "voucher requirements, header"
        })}
      />
      <CardContent>
        <RadioGroupField
          choices={requirementsPickerChoices}
          disabled={disabled}
          name={"requirementsPicker" as keyof VoucherDetailsPageFormData}
          value={data.requirementsPicker}
          onChange={onChange}
        />
        {[RequirementsPicker.ORDER, RequirementsPicker.ITEM].includes(
          data.requirementsPicker
        ) && <FormSpacer />}
        {data.requirementsPicker === RequirementsPicker.ORDER ? (
          <TextField
            id="minimalOrderValueTxt"
            disabled={disabled}
            error={!!formErrors.minSpent || !!clientErrors.minSpent}
            helperText={
              getDiscountErrorMessage(formErrors.minSpent, intl) ||
              clientErrors.minSpent
            }
            label={minimalOrderValueText}
            name={"minSpent" as keyof VoucherDetailsPageFormData}
            value={data.minSpent}
            onChange={onChange}
            fullWidth
          />
        ) : data.requirementsPicker === RequirementsPicker.ITEM ? (
          <TextField
            id="minimalQuantityOfItemsTxt"
            disabled={disabled}
            error={
              !!formErrors.minCheckoutItemsQuantity ||
              !!clientErrors.minCheckoutItemsQuantity
            }
            helperText={
              getDiscountErrorMessage(
                formErrors.minCheckoutItemsQuantity,
                intl
              ) || clientErrors.minCheckoutItemsQuantity
            }
            label={minimalQuantityText}
            name={
              "minCheckoutItemsQuantity" as keyof VoucherDetailsPageFormData
            }
            value={data.minCheckoutItemsQuantity}
            onChange={onChange}
            fullWidth
          />
        ) : null}
        {showToggleSwitch ? (
          <FormControlLabel
            id="appliedOnEligableItemsToggle"
            control={
              <Switch
                checked={data.isAppliedOnEligibleItems}
                onChange={event =>
                  onChange({
                    target: {
                      name: "isAppliedOnEligibleItems",
                      value: event.target.checked
                    }
                  })
                }
                disabled={true}
                name={"requirementsPicker" as keyof VoucherDetailsPageFormData}
                color="primary"
              />
            }
            label={
              data.isAppliedOnEligibleItems
                ? intl.formatMessage({
                    defaultMessage:
                      "Applied to specific eligible products on cart",
                    description: "applies eligible specific items"
                  })
                : intl.formatMessage({
                    defaultMessage: "Applied to all items on cart",
                    description: "applies eligible to all items"
                  })
            }
          />
        ) : null}
      </CardContent>
      <CardContent className={classes.minTenantsWrapper}>
        <ControlledCheckbox
          id="minimumNumberOfTenantCheckbox"
          disabled={
            disabled ||
            data.verticalType === VoucherClassificationEnum.SM_FULFILLED
          }
          checked={data.hasMinTenants}
          label={intl.formatMessage({
            defaultMessage: "Minimum Number of Tenants"
          })}
          name={"hasMinTenants" as keyof VoucherDetailsPageFormData}
          onChange={onChange}
        />
        {data.hasMinTenants && (
          <TextField
            id="numberOfTenantTxt"
            className={classes.minTenantsTextField}
            disabled={disabled}
            error={!!clientErrors.minTenants}
            helperText={clientErrors.minTenants}
            label={intl.formatMessage({
              defaultMessage: "Number of Tenants",
              description: "numberOfTenants"
            })}
            name={"minTenants" as keyof VoucherDetailsPageFormData}
            value={data.minTenants}
            onChange={onChange}
            type="number"
          />
        )}
      </CardContent>
    </Card>
  );
};
export default VoucherRequirements;
